import { QualityFormulaType } from '@local/lib';
import { Badge, Divider, Group, Paper, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { Lock } from 'lucide-react';

import {
  CONTRACT_MODE,
  CONTRACT_NUMBER,
  CONTRACT_PRICE,
  CONTRACT_PRICE_COMPONENTS,
  CONTRACT_QUANTITY,
  CONTRACT_UNIT_OF_MEASURE,
  CONTRACT_UNIT_PER_MODE,
  FORMULA_TYPES,
} from '../qualityFormulaMetadata';
import { ResultField } from './ResultField';
import { formatCurrency, formatNumber } from './utils/resultFormatting';

/**
 * Stand-in for a contract's actual price components, which this repo has no live per-contract
 * breakdown for yet (complex, index-based pricing is still design-only — see
 * docs/COMPLEX_PRICING.md). Hardcoded here purely so the section above Quality Adjustments reads
 * as a believable "previous step" rather than an empty placeholder; once real contract data
 * exists, this section should read from it instead. `formulaType` is a real `QualityFormulaType`,
 * so its label/hint come from the same `FORMULA_TYPES` metadata the Quality Adjustment "Formula
 * Type" field uses rather than an invented description. `contractNumber`/`priceComponents`/
 * `totalContractPrice`/`quantity`/`unitPerMode`/`unitOfMeasure` come from
 * `qualityFormulaMetadata.ts`'s shared contract constants so this summary, the "Price Components"
 * Price Basis's Add Component picker, and the Test Formula playground's quantity/unit/starting
 * price all read the same example contract.
 */
const CONTRACT_PRICING_EXAMPLE = {
  contractNumber: CONTRACT_NUMBER,
  formulaType: QualityFormulaType.Percentage,
  mode: CONTRACT_MODE,
  priceComponents: CONTRACT_PRICE_COMPONENTS,
  product: 'PetCoke',
  quantity: CONTRACT_QUANTITY,
  totalContractPrice: CONTRACT_PRICE,
  unitOfMeasure: CONTRACT_UNIT_OF_MEASURE,
  unitPerMode: CONTRACT_UNIT_PER_MODE,
};

/**
 * Read-only "Contract Pricing Summary" shown above Quality Adjustment configuration so the
 * Quality Formula Playground reads as one step in a larger business workflow (Contract Setup →
 * Quality Adjustment Configuration → Playground) instead of a standalone tool. Entirely
 * presentational — see `CONTRACT_PRICING_EXAMPLE`'s own doc comment for why its values are
 * hardcoded rather than fetched.
 */
export const ContractPricingSummarySection: React.FC = () => {
  const formulaTypeOption = FORMULA_TYPES.find(option => option.value === CONTRACT_PRICING_EXAMPLE.formulaType);

  return (
    <Stack aria-label="Contract pricing summary" component="section" gap="sm">
      <Stack gap={4}>
        <Title order={2} size="h4">
          Contract Setup
        </Title>
        <Text c="dimmed" fz="sm" maw={640}>
          Shown for context only — this is the contract pricing already established before quality adjustments are
          configured.
        </Text>
      </Stack>

      <Paper p="md" radius="md" style={{ borderStyle: 'dashed' }} withBorder>
        <Stack gap="sm">
          <Group justify="space-between" wrap="wrap">
            <Group gap="lg" wrap="wrap">
              <Group gap="xs">
                <Text c="dimmed" fw={700} fz={11} lts={0.3} tt="uppercase">
                  Contract #
                </Text>
                <Text fw={600} fz="sm">
                  {CONTRACT_PRICING_EXAMPLE.contractNumber}
                </Text>
              </Group>
              <Group gap="xs">
                <Text c="dimmed" fw={700} fz={11} lts={0.3} tt="uppercase">
                  Product
                </Text>
                <Text fw={600} fz="sm">
                  {CONTRACT_PRICING_EXAMPLE.product}
                </Text>
              </Group>
              <Group gap="xs">
                <Text c="dimmed" fw={700} fz={11} lts={0.3} tt="uppercase">
                  Quantity
                </Text>
                <Text fw={600} fz="sm">
                  {formatNumber(CONTRACT_PRICING_EXAMPLE.quantity)} {CONTRACT_PRICING_EXAMPLE.unitOfMeasure}
                </Text>
              </Group>
              <Group gap="xs">
                <Text c="dimmed" fw={700} fz={11} lts={0.3} tt="uppercase">
                  Mode
                </Text>
                <Badge color="gray" radius="sm" variant="outline">
                  {CONTRACT_PRICING_EXAMPLE.mode}
                </Badge>
              </Group>
              <Group gap="xs">
                <Text c="dimmed" fw={700} fz={11} lts={0.3} tt="uppercase">
                  Units per Mode
                </Text>
                <Badge color="gray" radius="sm" variant="outline">
                  {formatNumber(CONTRACT_PRICING_EXAMPLE.unitPerMode)} {CONTRACT_PRICING_EXAMPLE.unitOfMeasure}
                </Badge>
              </Group>
            </Group>
            <Badge color="gray" leftSection={<Lock size={12} />} radius="sm" variant="light">
              Read-only
            </Badge>
          </Group>

          <Divider />

          <Stack gap={4}>
            <Text c="dimmed" fw={700} fz={11} lts={0.3} tt="uppercase">
              Contract Pricing Formula Type
            </Text>
            <Group gap="xs" wrap="wrap">
              <Badge color="blue" radius="sm" variant="light">
                {formulaTypeOption?.label}
              </Badge>
              <Text c="dimmed" fz="sm">
                {formulaTypeOption?.hint}
              </Text>
            </Group>
          </Stack>

          <Divider />

          <Stack gap="xs">
            <Text c="dimmed" fw={700} fz={11} lts={0.3} tt="uppercase">
              Price Components
            </Text>
            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
              {CONTRACT_PRICING_EXAMPLE.priceComponents.map(component => (
                <ResultField
                  key={component.key}
                  label={component.label}
                  value={formatCurrency(component.value * component.sign)}
                />
              ))}
            </SimpleGrid>
          </Stack>

          <Paper bg="var(--mantine-color-blue-light)" p="sm" radius="sm" withBorder>
            <Group justify="space-between">
              <Text fw={700} fz="sm">
                Contract Price
              </Text>
              <Text fw={700} fz="md">
                {formatCurrency(CONTRACT_PRICING_EXAMPLE.totalContractPrice)} / {CONTRACT_PRICING_EXAMPLE.unitOfMeasure}
              </Text>
            </Group>
          </Paper>
        </Stack>
      </Paper>
    </Stack>
  );
};
